// @flow
import type {
  SetAction,
  DelKeysAction,
  DelAllKeysAction
} from '../../modules/hash'
import type { EntityMap } from '../../modules/entities'
import { SubmissionError } from 'redux-form'
import { normalize } from 'normalizr'
import client, { services } from '../../core/feathers'
import { hset, hmset, hdel, hdelAll } from '../../modules/hash'
import { mergeEntities } from '../../modules/entities'
import {
  startCommunication,
  endCommunication,
  isCommInProgress
} from '../../modules/communication/'
import { batch } from '../../modules/lib/batchReducer'
import { saveWinningBids, saveOutbidBids } from '../bid'
import { domains, schemas } from '../constants'
import { ThunkAction } from 'redux-thunk'
import { getPaymentMethods } from './index'
import {
  FETCH_SESSION_REQUEST,
  FETCH_SESSION_ABORT_NO_TOKEN,
  FETCH_SESSION_SUCCESS,
  FETCH_SESSION_FAILED,
  FETCH_USER_DATA_RESOURCE,
  FETCH_SESSION_RESOURCE,
  FETCH_USER_DATA_REQUEST,
  FETCH_USER_DATA_SUCCESS,
  FETCH_USER_DATA_FAILED,
  SAVE_USER_ID,
  SAVE_USER_DATA,
  CLEAR_SESSION
} from './constants'
import appHistory from '../../core/history'

type RegisterData = {
  email: string,
  name: string,
  lastName: string,
  password: string
}

export const register = ({
  email,
  name,
  lastName,
  password
}: RegisterData): ThunkAction =>
  async function doRegister(dispatch, getState) {
    try {
      await services.users.create({
        email,
        name,
        lastName,
        password
      })
      await dispatch(login({ email, password }))
      return true
    } catch (error) {
      if (/email/i.test(error.message)) {
        throw new SubmissionError({
          email: 'Email is already in use'
        })
      } else {
        throw new SubmissionError({ _error: 'Registration failed' })
      }
    }
  }
type LoginData = {
  email: string,
  password: string
}
export const login = ({ email, password }: LoginData): ThunkAction =>
  async function(dispatch) {
    try {
      await client.authenticate({
        strategy: 'local',
        email,
        password
      })
      await dispatch(fetchSession())
      //history && history.goBack()
      return true
    } catch (error) {
      // throw redux-form specific error
      // it can be catched in the UI
      throw new SubmissionError({ _error: 'Login failed' })
    }
  }

export const logout = (): ThunkAction =>
  function(dispatch) {
    client.logout().then(() => {
      appHistory.replace('/explore')
    })
  }

export const clearSession = (): DelAllKeysAction =>
  batch(CLEAR_SESSION, hdelAll(domains.SESSION))

export const checkSession = (): ThunkAction =>
  async function(dispatch) {
    try {
      return dispatch(fetchSession())
    } catch (e) {
      return false
    }
  }

export function fetchSession(): ThunkAction {
  return async function(dispatch, getState) {
    const resource = FETCH_SESSION_RESOURCE
    if (isCommInProgress(resource, getState())) {
      return false
    }
    try {
      dispatch(batch(FETCH_SESSION_REQUEST, startCommunication(resource)))
      const accessToken = await client.passport.getJWT()
      if (!accessToken) {
        dispatch(
          batch(FETCH_SESSION_ABORT_NO_TOKEN, endCommunication(resource, false))
        )
        return false
      }
      const payload = await client.passport.verifyJWT(accessToken)

      await client.authenticate({
        strategy: 'jwt',
        accessToken
      })
      dispatch(
        batch(SAVE_USER_ID, hset(domains.SESSION, 'userId', payload.userId))
      )
      await dispatch(fetchUserData(payload.userId))
      client.emit('login', payload.userId)
    } catch (e) {
      console.warn('fetching session failed.', e)
      //console.log('Logging out')
      //dispatch(logout())
      dispatch(batch(FETCH_SESSION_FAILED, endCommunication(resource, false)))
      return false
    }
    dispatch(batch(FETCH_SESSION_SUCCESS, endCommunication(resource)))
    return true
  }
}

type NormalizedBids = {
  entities: EntityMap<Object>,
  result: number[]
}
const normalizeBids = (bidsData = []): NormalizedBids => {
  return normalize(bidsData, [schemas.bids])
}

export const fetchUserData = (userId: string): ThunkAction =>
  async function(dispatch, getState) {
    const resource = FETCH_USER_DATA_RESOURCE
    if (isCommInProgress(resource, getState())) {
      return false
    }
    try {
      dispatch(batch(FETCH_USER_DATA_REQUEST, startCommunication(resource)))
      const session = await services.session.get(userId)
      dispatch(batch(FETCH_USER_DATA_SUCCESS, endCommunication(resource)))
      const {
        charityId,
        paymentMethods,
        name,
        lastName,
        clientToken,
        userAddresses,
        winning,
        outbid,
        email,
        isVerified,
        phone
      } = session
      const normalizedWinning = normalizeBids(winning)
      const normalizedOutbid = normalizeBids(outbid)
      dispatch(
        batch(
          SAVE_USER_DATA,
          mergeEntities(normalizedWinning.entities),
          saveWinningBids(winning),
          mergeEntities(normalizedOutbid.entities),
          saveOutbidBids(outbid),
          saveUserCharityId(charityId),
          hmset(domains.SESSION, {
            paymentMethods,
            userAddresses,
            name,
            lastName,
            email,
            isVerified,
            phone,
            clientToken
          })
        )
      )
      return true
    } catch (e) {
      console.warn('fetching session failed', e)
      dispatch(batch(FETCH_USER_DATA_FAILED, endCommunication(resource, false)))
      dispatch(logout())
      return false
    }
  }

export function savePaymentMethod(token: Object): ThunkAction {
  return async function(dispatch, getState) {
    // console.log('Saving payment method', token)
    // see shape at https://stripe.com/docs/api#source_object
    try {
      const result = await services.paymentMethod.create({
        token: token.id
      })
      //console.log('Payment method saved', result)
      const currentPaymentMethods = getPaymentMethods(getState())
      dispatch(
        hset(domains.SESSION, 'paymentMethods', [
          ...currentPaymentMethods,
          result
        ])
      )
      return true
    } catch (error) {
      //console.warn('Error saving payment method', error.message)
      throw error
    }
  }
}

export function saveUserCharityId(charityId: number): SetAction {
  return hset(domains.SESSION, 'charityId', charityId)
}

export function deleteUserCharityId(charityId: number): DelKeysAction {
  return hdel(domains.SESSION, 'charityId')
}



// WEBPACK FOOTER //
// ./src/services/session/actions.js